import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { NavHeader } from "../components/NavHeader";
import { Footer } from "../components/Footer";
import { ZODIAC, ZODIAC_SLUGS } from "../data/zodiacContent";

export const ZodiacHubPage = () => {
  useEffect(() => {
    document.title = "The 12 Zodiac Signs · Traits, Love, Career · Astrarium";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "The twelve zodiac signs, explained honestly. Traits, strengths, shadows, love and career patterns, plus lucky day, colours and gemstones for every sign.");
  }, []);

  return (
    <>
      <NavHeader />
      <main
        data-testid="zodiac-hub-page"
        className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 pt-28 sm:pt-32 pb-12"
      >
        <p className="text-stardust/70 uppercase tracking-[0.5em] text-[10px] mb-5 text-center">The twelve signs</p>
        <h1 className="font-display text-4xl sm:text-6xl text-[#F5EBD8] text-center leading-tight mb-5">
          Every sign of the zodiac<span className="text-stardust">.</span>
        </h1>
        <p className="font-serif-italic text-slate-300/90 text-lg sm:text-2xl leading-relaxed text-center mb-14 max-w-2xl mx-auto">
          Aries to Pisces. Traits, love, career, lucky forecast. Written straight,
          no cosmic filler.
        </p>

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {ZODIAC_SLUGS.map((slug, i) => {
            const s = ZODIAC[slug];
            return (
              <motion.div
                key={slug}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.03 }}
              >
                <Link
                  to={`/zodiac/${slug}`}
                  data-testid={`zodiac-hub-card-${slug}`}
                  className="group block p-6 rounded-xl bg-white/[0.02] border border-white/10 hover:border-stardust/50 hover:bg-white/[0.04] transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-display text-2xl text-[#F5EBD8] group-hover:text-stardust transition-colors leading-tight">
                        {s.name}
                      </p>
                      <p className="text-slate-500 text-[10px] uppercase tracking-[0.25em] mt-1">{s.dates}</p>
                    </div>
                    <span
                      className="text-stardust text-3xl group-hover:scale-110 transition-transform"
                      style={{ fontFamily: "'DM Serif Display', serif" }}
                    >
                      {s.symbol + "\uFE0E"}
                    </span>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed line-clamp-3">{s.essence}</p>
                  <p className="text-stardust/80 uppercase tracking-[0.25em] text-[10px] mt-4">
                    {s.element} · {s.modality} · Ruled by {s.ruling_planet.split(" ")[0]}
                  </p>
                </Link>
              </motion.div>
            );
          })}
        </section>

        <section className="mt-16 sm:mt-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl text-[#F5EBD8] mb-3 leading-tight">
            Not sure about your sign?
          </h2>
          <p className="text-slate-300 leading-relaxed max-w-2xl mx-auto mb-8">
            Enter your date and place of birth and Astrarium draws the full almanac,
            not just the sun sign. Love, career, family, finances, and the year ahead.
          </p>
          <Link
            to="/"
            data-testid="zodiac-hub-cta"
            className="inline-flex items-center bg-stardust hover:bg-stardust-light text-cosmic-900 rounded-md uppercase tracking-[0.3em] text-xs px-10 py-5 font-medium shadow-[0_0_28px_rgba(226,194,117,0.25)] hover:shadow-[0_0_44px_rgba(226,194,117,0.45)] transition-all"
          >
            Get my reading ✦
          </Link>
        </section>
      </main>
      <Footer />
    </>
  );
};
